import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Plus, Edit2, Trash2, MapPin, AlertTriangle, Compass, Search } from 'lucide-react';
import { Table } from '../components/ui/Table';
import {
  AccommodationRecord,
  adminListAccommodations,
  createAccommodation,
  deleteAccommodation,
  geocodeAccommodations,
  getResortLocations,
  updateAccommodation,
} from '../services/resortAdminApi';
import { useAuth } from '../context/AuthContext';
import { describeAccommodationError } from '../utils/accommodationErrors';
import {
  LATITUDE_LIMIT,
  LONGITUDE_LIMIT,
  isCoordinatePairInRange,
  parseCoordinate,
} from '../utils/coordinates';

/**
 * Catalogue of known chalets / hotels / apartments guests can pick by name at
 * checkout instead of typing a street address.
 *
 * Coordinates are what make an entry usable: the client app only offers
 * accommodations that fall inside the merchant's delivery zone, and the order
 * endpoint re-checks them. An entry without coordinates is reachable by resort
 * only, so the table surfaces that state prominently.
 */

const PAGE_SIZE = 50;
const SEARCH_DEBOUNCE_MS = 300;

/** Shape returned by `get_resort_locations`: `{ value: docname, label: display }`. */
interface ResortOption {
  value: string;
  label: string;
}

interface FormState {
  accommodation_name: string;
  address: string;
  resort: string;
  latitude: string;
  longitude: string;
  google_maps_link: string;
  is_active: boolean;
}

const emptyForm: FormState = {
  accommodation_name: '',
  address: '',
  resort: '',
  latitude: '',
  longitude: '',
  google_maps_link: '',
  is_active: true,
};

const toFormState = (row: AccommodationRecord): FormState => ({
  accommodation_name: row.accommodation_name,
  address: row.address || '',
  resort: row.resort || '',
  // A 0 here means "never geocoded", not "the Gulf of Guinea": Frappe Float columns
  // are NOT NULL DEFAULT 0. Prefilling it would let a save pin 0,0 as Manual, after
  // which nothing re-checks the row. The API normalises this too; this is the belt.
  latitude: row.latitude ? String(row.latitude) : '',
  longitude: row.longitude ? String(row.longitude) : '',
  google_maps_link: row.google_maps_link || '',
  is_active: !!row.is_active,
});


export const Accommodations: React.FC = () => {
  const { user, isReadOnly } = useAuth();
  const canWrite = (user?.role === 'Resort Super Admin' || user?.role === 'Editor') && !isReadOnly;

  const [rows, setRows] = useState<AccommodationRecord[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  // The field value updates on every keystroke; the request only follows the
  // debounced value, so typing a chalet name is one request rather than twenty.
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [resortFilter, setResortFilter] = useState('');
  const [resorts, setResorts] = useState<ResortOption[]>([]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [showFormModal, setShowFormModal] = useState(false);
  const [editing, setEditing] = useState<AccommodationRecord | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [saving, setSaving] = useState(false);

  const [showDeleteModal, setShowDeleteModal] = useState<{ show: boolean; row: AccommodationRecord | null }>({
    show: false,
    row: null,
  });
  const [deleting, setDeleting] = useState(false);
  const [geocoding, setGeocoding] = useState(false);
  const requestSeq = useRef(0);

  const loadRows = useCallback(async () => {
    const requestId = requestSeq.current + 1;
    requestSeq.current = requestId;
    const isLatest = () => requestSeq.current === requestId;

    setLoading(true);
    setError('');
    try {
      const result = await adminListAccommodations({
        query: searchTerm.trim(),
        resort: resortFilter,
        limit: PAGE_SIZE,
        offset: (page - 1) * PAGE_SIZE,
      });
      // A slower earlier request must not overwrite the page the admin is
      // already looking at.
      if (!isLatest()) return;
      setRows(Array.isArray(result?.items) ? result.items : []);
      setTotal(Number(result?.total) || 0);
    } catch (e: unknown) {
      if (!isLatest()) return;
      setError(describeAccommodationError(e, 'Failed to load accommodations'));
    } finally {
      if (isLatest()) setLoading(false);
    }
  }, [page, resortFilter, searchTerm]);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setPage(1);
      setSearchTerm(searchInput);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    getResortLocations()
      // The endpoint wraps the list: { resort_locations: [{ value, label }], total_locations }.
      .then((result) => {
        const list = Array.isArray(result) ? result : result?.resort_locations;
        setResorts(Array.isArray(list) ? list : []);
      })
      .catch(() => setResorts([]));
  }, []);

  // A new search or resort filter always starts from the first page. The search
  // page reset happens with the debounced value, not on every keystroke.
  const handleSearch = (value: string) => {
    setSearchInput(value);
  };

  const handleResortFilter = (value: string) => {
    setPage(1);
    setResortFilter(value);
  };

  const missingCoordinates = useMemo(
    () => rows.filter((row) => !row.latitude || !row.longitude).length,
    [rows],
  );

  const openCreate = () => {
    setEditing(null);
    setForm({ ...emptyForm, resort: resortFilter });
    setError('');
    setShowFormModal(true);
  };

  const openEdit = (row: AccommodationRecord) => {
    setEditing(row);
    setForm(toFormState(row));
    setError('');
    setShowFormModal(true);
  };

  const closeForm = () => {
    setShowFormModal(false);
    setEditing(null);
    setForm(emptyForm);
    setError('');
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    const latitude = parseCoordinate(form.latitude);
    const longitude = parseCoordinate(form.longitude);
    if ((form.latitude.trim() && latitude === null) || (form.longitude.trim() && longitude === null)) {
      setError('Latitude and longitude must be numbers');
      return;
    }
    if ((latitude === null) !== (longitude === null)) {
      setError('Enter both latitude and longitude, or leave both empty to geocode from the address');
      return;
    }
    if (latitude !== null && longitude !== null && !isCoordinatePairInRange(latitude, longitude)) {
      setError(
        `Latitude must be between -${LATITUDE_LIMIT} and ${LATITUDE_LIMIT}, ` +
          `longitude between -${LONGITUDE_LIMIT} and ${LONGITUDE_LIMIT}`,
      );
      return;
    }

    setSaving(true);
    try {
      const payload = {
        accommodation_name: form.accommodation_name.trim(),
        address: form.address.trim(),
        resort: form.resort || null,
        google_maps_link: form.google_maps_link.trim() || null,
        is_active: form.is_active ? 1 : 0,
      };

      if (editing) {
        // Coordinates are only sent when the admin actually touched them:
        // re-sending the pre-filled values would pin them as manual and stop
        // the backend from re-geocoding after a later address change.
        const untouched = latitude === (editing.latitude ?? null) && longitude === (editing.longitude ?? null);
        const coordinates = untouched
          ? {}
          : latitude === null
            ? { clear_coordinates: true } // emptied → re-geocode from the address
            : { latitude, longitude }; // typed in → pin

        await updateAccommodation({
          accommodation_id: editing.name,
          ...payload,
          ...coordinates,
        });
        setSuccess(`"${payload.accommodation_name}" updated`);
      } else {
        await createAccommodation({ ...payload, latitude, longitude });
        setSuccess(`"${payload.accommodation_name}" created`);
      }
      closeForm();
      loadRows();
    } catch (e: unknown) {
      setError(describeAccommodationError(e, 'Failed to save accommodation'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!showDeleteModal.row) return;
    setDeleting(true);
    setError('');
    try {
      await deleteAccommodation(showDeleteModal.row.name);
      setSuccess(`"${showDeleteModal.row.accommodation_name}" deleted`);
      setShowDeleteModal({ show: false, row: null });
      loadRows();
    } catch (e: unknown) {
      setError(describeAccommodationError(e, 'Failed to delete accommodation'));
    } finally {
      setDeleting(false);
    }
  };

  const handleGeocodeMissing = async () => {
    setGeocoding(true);
    setError('');
    setSuccess('');
    try {
      const result = await geocodeAccommodations({ limit: 100, include_failed: true });
      // Only entries still missing coordinates are processed, so an empty run is
      // the normal outcome for a fully geocoded catalogue — say so plainly.
      const remaining = Number(result?.remaining ?? 0);
      setSuccess(
        (result?.processed ?? 0) === 0
          ? 'Nothing to geocode — every accommodation already has coordinates'
          : `Geocoding finished — ${result?.resolved ?? 0} resolved, ${result?.failed ?? 0} failed ` +
            `out of ${result.processed} processed` +
            // The batch is capped and stops on a deadline, so a non-zero
            // remainder means the button has to be pressed again.
            (remaining > 0 ? `. ${remaining} still waiting, press again to continue` : ''),
      );
      loadRows();
    } catch (e: unknown) {
      setError(describeAccommodationError(e, 'Failed to geocode accommodations'));
    } finally {
      setGeocoding(false);
    }
  };

  const columns = [
    {
      key: 'accommodation_name',
      header: 'Name',
      render: (_: unknown, row: AccommodationRecord) => (
        <span className="font-medium text-gray-800">{row.accommodation_name}</span>
      ),
    },
    {
      key: 'address',
      header: 'Address',
      render: (_: unknown, row: AccommodationRecord) => (
        <span className="text-sm text-gray-600">{row.address}</span>
      ),
    },
    {
      key: 'resort',
      header: 'Resort',
      render: (_: unknown, row: AccommodationRecord) =>
        row.resort ? (
          // `resort` is a docname, and on a site whose resorts were created in Desk
          // that is a hash like "2j8svgjp5g" rather than a name.
          // The readable name is the label; the docname stays available on hover for
          // anyone who needs it to look the record up in Desk.
          <span
            className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
            title={row.resort}
          >
            {row.resort_name || row.resort_label || row.resort}
          </span>
        ) : (
          <span
            className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600"
            title="No matching resort in the system — reachable through the merchant delivery zone only"
          >
            {row.resort_label || 'Unlinked'}
          </span>
        ),
    },
    {
      key: 'latitude',
      header: 'Coordinates',
      render: (_: unknown, row: AccommodationRecord) =>
        row.latitude && row.longitude ? (
          <span className="text-xs text-gray-600 whitespace-nowrap">
            {Number(row.latitude).toFixed(5)}, {Number(row.longitude).toFixed(5)}
            {row.geocode_status === 'Manual' && <span className="ml-1 text-gray-400">(manual)</span>}
          </span>
        ) : (
          <span className="inline-flex items-center text-xs font-medium text-amber-700">
            <AlertTriangle className="h-3.5 w-3.5 mr-1" />
            Missing
          </span>
        ),
    },
    {
      key: 'is_active',
      header: 'Status',
      render: (_: unknown, row: AccommodationRecord) =>
        row.is_active ? (
          <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
            Active
          </span>
        ) : (
          <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-gray-200 text-gray-600">
            Inactive
          </span>
        ),
    },
    {
      key: 'actions',
      header: 'Actions',
      render: (_: unknown, row: AccommodationRecord) => (
        <div className="flex space-x-2">
          <button
            onClick={() => openEdit(row)}
            disabled={!canWrite}
            className="text-indigo-600 hover:text-indigo-900 disabled:opacity-50 disabled:cursor-not-allowed"
            title="Edit"
          >
            <Edit2 className="h-4 w-4" />
          </button>
          <button
            onClick={() => setShowDeleteModal({ show: true, row })}
            disabled={!canWrite}
            className="text-red-600 hover:text-red-900 disabled:opacity-50 disabled:cursor-not-allowed"
            title="Delete"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-semibold text-gray-800">Accommodations</h1>
          <p className="mt-1 text-sm text-gray-600">
            Chalets, hotels and apartments guests can pick by name at checkout
          </p>
        </div>
        {canWrite && (
          <div className="flex items-center space-x-3">
            <button
              onClick={handleGeocodeMissing}
              disabled={geocoding}
              className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              title="Resolve coordinates for entries that don't have them yet"
            >
              <Compass className="h-4 w-4 mr-2" />
              {geocoding ? 'Geocoding...' : 'Geocode missing'}
            </button>
            <button
              onClick={openCreate}
              className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-alps-primary hover:bg-alps-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-alps-primary"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Accommodation
            </button>
          </div>
        )}
      </div>

      {/* Alerts */}
      {error && (
        <div className="bg-red-50 border-l-4 border-red-500 p-4">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}
      {success && (
        <div className="bg-green-50 border-l-4 border-green-500 p-4">
          <p className="text-sm text-green-700">{success}</p>
        </div>
      )}
      {!loading && missingCoordinates > 0 && (
        <div className="bg-amber-50 border-l-4 border-amber-500 p-4">
          <p className="text-sm text-amber-800">
            {missingCoordinates} of the accommodations on this page have no coordinates. They can only be
            found by guests ordering in the same resort, and cannot be checked against a delivery zone.
          </p>
        </div>
      )}

      {/* Search + resort filter, on one row */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="relative w-full sm:w-96">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search className="h-5 w-5 text-gray-400" />
          </div>
          <input
            type="text"
            value={searchInput}
            onChange={(e) => handleSearch(e.target.value)}
            placeholder="Search by name or address..."
            className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-alps-primary focus:border-alps-primary sm:text-sm"
          />
        </div>
        <select
          value={resortFilter}
          onChange={(e) => handleResortFilter(e.target.value)}
          className="w-full sm:w-56 border border-gray-300 rounded-md shadow-sm py-2 px-3 text-sm bg-white focus:outline-none focus:ring-alps-primary focus:border-alps-primary"
        >
          <option value="">All resorts</option>
          {resorts.map((resort) => (
            <option key={resort.value} value={resort.value}>
              {resort.label}
            </option>
          ))}
        </select>
      </div>

      {/* Table */}
      <Table
        columns={columns}
        data={rows}
        isLoading={loading}
        emptyMessage="No accommodations found"
        pagination={{ page, page_size: PAGE_SIZE, total }}
        onPageChange={setPage}
      />

      {/* Create / Edit Modal */}
      {showFormModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold">
                {editing ? 'Edit Accommodation' : 'Add Accommodation'}
              </h2>
              <button onClick={closeForm} className="text-gray-500 hover:text-gray-700">
                ×
              </button>
            </div>
            <form onSubmit={handleSave} className="space-y-4">
              {/* A refused save leaves this modal open, and the page-level banner is
                  behind the overlay, so the only place the admin can read the reason
                  is here. */}
              {error && (
                <div className="bg-red-50 border-l-4 border-red-500 p-3">
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={form.accommodation_name}
                  onChange={(e) => setForm({ ...form, accommodation_name: e.target.value })}
                  placeholder="Alpenrose Chamonix"
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-alps-primary focus:border-alps-primary sm:text-sm"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Address <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={form.address}
                  onChange={(e) => setForm({ ...form, address: e.target.value })}
                  rows={2}
                  placeholder="1141 Rte des Pèlerins, 74400 Chamonix-Mont-Blanc"
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-alps-primary focus:border-alps-primary sm:text-sm"
                  required
                />
                <p className="mt-1 text-xs text-gray-500">
                  Coordinates are resolved from this address when you save, unless you enter them below.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Resort</label>
                <select
                  value={form.resort}
                  onChange={(e) => setForm({ ...form, resort: e.target.value })}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-alps-primary focus:border-alps-primary sm:text-sm"
                >
                  <option value="">No resort</option>
                  {resorts.map((resort) => (
                    <option key={resort.value} value={resort.value}>
                      {resort.label}
                    </option>
                  ))}
                </select>
                {editing?.resort_label && !form.resort && (
                  <p className="mt-1 text-xs text-gray-500">
                    Imported under "{editing.resort_label}", which has no resort in the system yet.
                  </p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Latitude</label>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={form.latitude}
                    onChange={(e) => setForm({ ...form, latitude: e.target.value })}
                    placeholder="45.911714"
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-alps-primary focus:border-alps-primary sm:text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Longitude</label>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={form.longitude}
                    onChange={(e) => setForm({ ...form, longitude: e.target.value })}
                    placeholder="6.855047"
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-alps-primary focus:border-alps-primary sm:text-sm"
                  />
                </div>
              </div>
              <p className="text-xs text-gray-500 flex items-start">
                <MapPin className="h-3.5 w-3.5 mr-1 mt-0.5 flex-shrink-0" />
                Leave both empty to geocode from the address. Entered coordinates are kept as-is and never
                overwritten.
              </p>

              <div>
                <label className="block text-sm font-medium text-gray-700">Google Maps link</label>
                <input
                  type="text"
                  value={form.google_maps_link}
                  onChange={(e) => setForm({ ...form, google_maps_link: e.target.value })}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-alps-primary focus:border-alps-primary sm:text-sm"
                />
              </div>

              <div className="flex items-center space-x-3">
                <input
                  id="accommodation-active"
                  type="checkbox"
                  checked={form.is_active}
                  onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                  className="h-4 w-4 text-alps-primary border-gray-300 rounded focus:ring-alps-primary"
                />
                <label htmlFor="accommodation-active" className="text-sm text-gray-700">
                  Active <span className="text-gray-400">(offered in the client app address search)</span>
                </label>
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={closeForm}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving || !form.accommodation_name.trim() || !form.address.trim()}
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-alps-primary hover:bg-alps-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-alps-primary disabled:opacity-50"
                >
                  {saving ? 'Saving...' : editing ? 'Save' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal.show && showDeleteModal.row && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-red-600">Delete Accommodation</h2>
              <button
                onClick={() => setShowDeleteModal({ show: false, row: null })}
                className="text-gray-500 hover:text-gray-700"
              >
                ×
              </button>
            </div>
            <p className="text-gray-600 mb-4">
              Delete "{showDeleteModal.row.accommodation_name}"? Guests will no longer find it by name.
              Orders already placed keep the address they were delivered to. This action cannot be undone.
            </p>
            {error && (
              <div className="bg-red-50 border-l-4 border-red-500 p-3 mb-4">
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowDeleteModal({ show: false, row: null })}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
              >
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
